"use client";

import Image from "next/image";
import type { PublicCertificationItem } from "@/lib/services/certification.service";

function buildGoogleSearchUrl(company: string) {
	return `https://www.google.com/search?q=${encodeURIComponent(company.trim())}`;
}

export default function CertificationCard({
	certification,
	index,
}: {
	certification: PublicCertificationItem;
	index: number;
}) {
	const issuerGoogleUrl = buildGoogleSearchUrl(certification.organization);
	const imageOnRight = index % 2 === 0;

	return (
		<article className="group relative overflow-hidden rounded-xl border border-dashed border-white/10 bg-[#0a0d13]/80">
			<p className="pointer-events-none absolute -right-4 top-1/2 -translate-y-1/2 text-6xl font-semibold uppercase tracking-tight text-white/5">
				Cert
			</p>
			<div
				className={`grid gap-0 ${
					imageOnRight ? "md:grid-cols-[1fr_320px]" : "md:grid-cols-[320px_1fr]"
				}`}
			>
				<div
					className={`relative min-h-44 border-b border-white/10 md:min-h-full md:border-b-0 ${
						imageOnRight
							? "md:order-2 md:border-l md:border-r-0"
							: "md:order-1 md:border-r md:border-l-0"
					}`}
				>
					<div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.07),transparent_70%)]" />
					<div className="relative h-full w-full p-3">
						<div className="relative h-full min-h-48 overflow-hidden rounded-xl border border-white/10 bg-zinc-900">
							<Image
								src={certification.imageUrl}
								alt={certification.title}
								fill
								unoptimized
								className="object-cover transition duration-500 group-hover:scale-105"
								sizes="(max-width: 768px) 100vw, 320px"
							/>
						</div>
					</div>
				</div>

				<div className={`${imageOnRight ? "md:order-1" : "md:order-2"} p-4 md:p-6`}>
					<div className="max-w-2xl">
						<h3 className="font-rethink text-2xl font-semibold text-white">
							{certification.title}
						</h3>
						<p className="mt-2 line-clamp-2 text-sm leading-relaxed text-zinc-300">
							{certification.description}
						</p>
						<div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-zinc-400">
							<span>Issuer:</span>
							<a
								href={issuerGoogleUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex cursor-ne-resize cursor-apple-ne-resize items-center gap-1 underline underline-offset-2 transition hover:text-white"
							>
								{certification.organization}
							</a>
						</div>
					</div>

					{certification.credentialUrl ? (
						<div className="mt-5 flex items-center">
							<a
								href={certification.credentialUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-[linear-gradient(160deg,rgba(255,255,255,0.07),rgba(255,255,255,0.02))] px-5 py-2 text-sm text-zinc-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition hover:border-white/40 hover:bg-[linear-gradient(160deg,rgba(255,255,255,0.1),rgba(255,255,255,0.04))]"
							>
								View Credential
							</a>
						</div>
					) : null}
				</div>
			</div>
		</article>
	);
}
